import React, { useState, useRef, useEffect } from "react";
import AnnouncementCard from "./AnnouncementCard";
import AnouncementModal from "./AnouncementModal";
import "./Announcements.css";
import { motion } from "framer-motion";
import axiosInstance from "../../axiosinstance";
import {
  Ann,
  Med,
  Mreel,
  PCast,
  Pint,
  Preel,
  Qreel,
  Quo,
} from "../../images/images";
import { useAutoHorizontalScroll } from "../../hooks/useAutoHorizontalScroll";
import { fadeIn, staggerContainer } from "../../utils/motion";
import HorizontalScroll from "../HorizontalScroll/HorizontalScroll";

// Standalone podcast array (can contain more podcast items)
const podcastItems = [
  {
    image: PCast,
    desc: "",
    price: "$40",
    title: "",
    type: "ministry",
  },
  // ...add more podcast items here if needed
];

// Other ministry items (in original order)
const otherMinistryItems = [
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/62.png?v=1745672506&width=750",
    desc: "Short Sleeve T-shirt",
    price: "$30",
    title: "T-shirt",
    type: "ministry",
  },
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/29_2.png?v=1745667571&width=750",
    desc: "Necklace - Jewelry",
    price: "$30",
    title: "Jewelry",
    type: "ministry",
  },
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/59.png?v=1745672298&width=750",
    desc: "Praying is never a waste of time",
    price: "$30",
    title: "T-shirt",
    type: "ministry",
  },
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/il_794xN.5879484970_3t0u.jpg?v=1745666417&width=750",
    desc: "PRICE INCLUDES TAX - Faith Journal - Diario de Fe. Leather Ring Binder Cover - 6 Ring Organiser. Templates: Spanish, English, or Portuguese",
    price: "$30",
    title: "Book",
    type: "ministry",
  },
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/58.png?v=1745672234&width=750",
    desc: "Choosing GOD is never a waste of time tee",
    price: "$30",
    title: "T-shirt",
    type: "ministry",
  },
  {
    image:
      "https://chantal-ekabe-ministries-store.myshopify.com/cdn/shop/files/57.png?v=1745672169&width=750",
    desc: "I can do all things tee",
    price: "$30",
    title: "T-shirt",
    type: "ministry",
  },
];

const socialMedia = [
  {
    platform: "pinterest",
    logo: Preel,
    text: "Follow us on Pinterest",
    link: "https://www.pinterest.com/chantalekabeministries/_saved/",
    image: Pint,
  },

  {
    platform: "quora",
    logo: Qreel,
    text: "Follow us on Quora",
    link: "https://www.quora.com/profile/CHANTAL-EKABE-MINISTRIES/",
    image: Quo,
  },

  {
    platform: "medium",
    logo: Mreel,
    text: "Follow us on Medium",
    link: "https://medium.com/@info_93047",
    image: Med,
  },
];

interface AnnouncementsProps {
  dummyAnnouncements?: any[];
}

const Announcements: React.FC<AnnouncementsProps> = ({
  dummyAnnouncements = [],
}) => {
  const [reels, setReels] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>(null);

  const scrollRef = useAutoHorizontalScroll({
    speed: 0.5,
    pauseOnHover: true,
  });

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    let active = true;
    axiosInstance.get<any[]>("/spotlights")
      .then(({ data }) => {
        if (!active) return;
        setReels(data.map((spotlight) => ({
          _id: spotlight._id,
          title: spotlight.title,
          desc: spotlight.desc || "",
          thumbnail: spotlight.thumbnail?.url || Ann,
          link: spotlight.link,
          type: "reel",
          logo: "",
          platform: spotlight.platform,
          // Set per spotlight in the admin (Media Spotlight → button text)
          buttonText: spotlight.buttonText || spotlight.button_text || "Watch Reel",
        })));
      })
      .catch(() => { if (active) setReels([]); });
    return () => { active = false; };
  }, []);

  // Check scroll position
  const checkScroll = () => {
    const node = scrollRef.current;
    if (node) {
      setCanScrollLeft(node.scrollLeft > 0);
      setCanScrollRight(node.scrollLeft + node.clientWidth < node.scrollWidth);
    }
  };

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    checkScroll();
    node.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      node.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [scrollRef]);

  const handleReadMore = (item: any) => {
    if (item.type === "reel") {
      window.open(item.link, "_blank", "noopener,noreferrer");
    } else {
      setSelectedAnnouncement(item);
      setModalOpen(true);
    }
  };

  // Final combined items: just concatenate all arrays in their original order
  const combinedItems = [
    ...dummyAnnouncements,
    ...reels,
    ...podcastItems.map((item, idx) => ({
      _id: `podcast-${idx}`,
      ...item,
    })),
    ...otherMinistryItems.map((item, idx) => ({
      _id: `ministry-${idx}`,
      ...item,
    })),
    ...socialMedia.map((item, idx) => ({
      _id: `social-${idx}`,
      type: "social",
      ...item,
    })),
  ];

  return (
    <motion.section
      className="announcements-section"
      variants={staggerContainer(0.2, 0.3)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.25 }}
    >
      <motion.h2
        className="announcements-header comp"
        variants={fadeIn("up", "spring", 0.1, 0.8)}
      >
        🎬 MEDIA SPOTLIGHTS
      </motion.h2>

      <div style={{ position: "relative" }}>
        <HorizontalScroll
          showArrows
          showIndicator={false}
          showArrowsOnDesktop={true}
          autoScroll
        >
          {combinedItems.map((item, idx) => {
            if (item.type === "announcement") {
              return (
                <div className="" key={item._id}>
                  <AnnouncementCard
                    image={item.image}
                    title={item.title}
                    link={item.link}
                    onReadMore={() => handleReadMore(item)}
                  />
                </div>
              );
            }

            if (item.type === "reel" && "thumbnail" in item && "logo" in item) {
              const reel = item as {
                thumbnail: string;
                link: string;
                desc: string;
                title?: string;
                logo: string;
                platform: string;
                buttonText?: string;
              };
              return (
                <div className="" key={item._id + idx}>
                  <div className="announcement-reel-card">
                    <div className="announcement-reel-card-img-wrapper">
                      <img
                        src={reel.thumbnail}
                        alt={reel.title}
                        className="announcement-reel-card-img"
                      />
                    </div>
                    <div className="announcement-reel-card-content">
                      <div className="announcement-reel-card-title">
                        {reel.title}
                      </div>
                      <div className="announcement-reel-card-desc">
                        {reel.desc}
                      </div>
                      <button
                        className="announcement-reel-card-btn"
                        onClick={() => handleReadMore(reel)}
                      >
                        {reel.buttonText || "Watch Reel"} <span aria-hidden="true">→</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            if (
              item.type === "social" &&
              "image" in item &&
              "platform" in item &&
              "text" in item
            ) {
              return (
                <div className="" key={item._id + idx}>
                  <div className="announcement-reel-card">
                    <div className="announcement-reel-card-img-wrapper">
                      <img
                        src={item.image}
                        alt={item.platform}
                        className="announcement-reel-card-img"
                      />
                    </div>
                    <div className="announcement-reel-card-content">
                      <div className="announcement-reel-card-title">
                        {item.text}
                      </div>
                      <button
                        className="announcement-reel-card-btn"
                        onClick={() => window.open(item.link, "_blank", "noopener,noreferrer")}
                      >
                        Visit <span aria-hidden="true">→</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            if (item.type === "ministry" && "image" in item) {
              return (
                <div className="horizontal-scroll-card" key={item._id + idx}>
                  <div className="announcement-reel-card">
                    <div className="announcement-reel-card-img-wrapper">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="announcement-reel-card-img"
                      />
                    </div>
                    <div className="announcement-reel-card-content">
                      <div className="announcement-reel-card-title">
                        {item.title}
                      </div>
                      <div className="announcement-reel-card-desc">
                        {item.desc}
                      </div>
                      <div className="announcement-reel-card-desc">
                        {item.price}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </HorizontalScroll>
      </div>

      <AnouncementModal
        open={modalOpen}
        announcement={selectedAnnouncement}
        onClose={() => setModalOpen(false)}
      />
    </motion.section>
  );
};

export default Announcements;
